import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import * as S from "./styles";
import * as API from "../../api";
import { toast } from "react-toastify";
import { FieldText } from "../FieldText";
import { FieldNumber } from "../FieldNumber";
import { FieldDate } from "../FieldDate";
import { FieldList } from "../FieldList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt, faMailBulk } from "@fortawesome/free-solid-svg-icons";
import { Dialog } from "../Dialog";
import { FieldCheckboxModulo } from "../FieldCheckboxModulo";
import { COLORES } from "../../constants";

export const RegistroArea = ({
  metadataTable,
  area: initialValues,
  listas,
  type,
  handleSelectionChange,
  selected,
  disabled,
  setLoading,
  update,
  access,
  showEditBuzon,
  onEditBuzon
}) => {
  const [editing, setEditing] = useState(false);
  const [area, setArea] = useState({});
  const [dialogComentarioVisible, setDialogComentarioVisible] = useState("");
  const form = useRef();

  useEffect(() => {
    setArea(initialValues);
  }, [initialValues]);

  const handleChange = (key, value) => {
    const newValues = { ...area };
    newValues[key] = value;
    setArea(newValues);
  };

  const handleChangeBoolean = (modulo, key, value) => {
    const newProveedor = { ...area };
    newProveedor[key] = value;
    setArea(newProveedor);
  };

  const cancelEdit = () => {
    setArea(initialValues);
    setEditing(false);
  };

  const saveArea = (event) => {
    event.preventDefault();
    setLoading(true);
    setEditing(false);

    const formData = new FormData(form.current);
    const objectFormData = Object.fromEntries(formData);
    objectFormData.ImporteNivel1 = area.ImporteNivel1;
    objectFormData.ImporteNivel2 = area.ImporteNivel2;
    objectFormData.ImporteNivel3 = area.ImporteNivel3;
    objectFormData.ImporteNivel4 = area.ImporteNivel4;
    objectFormData.id = area.id;
    objectFormData.fechaModificacion = area.fechaModificacion;
    API.guardarArea(objectFormData)
      .then((res) => {
        if (res.Estado === "OK") {
          handleChange("fechaModificacion", res.UltimaModificacion);
          toast.success("Área Guardada");
        } else if (res.Estado === "NeedReload") {
          toast.warning(
            "Se encontró una versión más reciente del área. Se requiere recargar"
          );
        } else {
          toast.info(res.Mensaje);
        }
      })
      .catch((error) => {
        toast.error("No se pudo guardar el área");
        API.DEVELOP && console.log(error);
      })
      .finally(() => setLoading(false));
  };

  const eliminarArea = () => {
    const data = { IdArea: area.id };

    setLoading(true);
    API.eliminarArea(data)
      .then((res) => {
        if (res.Estado === "OK") {
          handleChange("fechaModificacion", res.UltimaModificacion);
          toast.success("Área Eliminada");
          update();
        } else if (res.Estado === "NeedReload") {
          toast.warning(
            "Se encontró una versión más reciente del área. Se requiere recargar"
          );
        } else {
          toast.info(res.Mensaje);
        }
      })
      .catch((error) => {
        toast.error("Error al eliminar Área");
        API.DEVELOP && console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <S.Tr ref={form} onSubmit={saveArea} highlighted={editing || selected}>
      {type === "detalleProveedor" && (
        <S.Checkbox>
          <S.InputCheckbox
            type="checkbox"
            checked={selected}
            onChange={(e) => handleSelectionChange(area.id, e.target.checked)}
            disabled={disabled}
          />
        </S.Checkbox>
      )}
      {metadataTable.map((field) => {
        const type = field.tipo;
        const idField = field.nombreCampo;
        const value = area[field.nombreCampo] || "";
        const editable = field.editable;
        const required = field.requerido;
        switch (type) {
          case "text":
            return (
              <S.Td key={idField} title={value}>
                <FieldText
                  id={idField}
                  value={value}
                  handleChange={handleChange}
                  readOnly={!editable || !editing}
                  required={required}
                />
              </S.Td>
            );

          case "int":
          case "float":
            return (
              <S.Td key={idField} title={value}>
                <FieldNumber
                  id={idField}
                  value={value || 0}
                  empty={false}
                  handleChange={handleChange}
                  type={type}
                  readOnly={!editable || !editing}
                  required={required}
                />
              </S.Td>
            );

          case "date":
          case "datetime":
            return (
              <S.Td key={idField} title={value}>
                <FieldDate
                  id={idField}
                  value={value.split("T")[0] || ""}
                  handleChange={handleChange}
                  readOnly={!editable || !editing}
                  required={required}
                />
              </S.Td>
            );

          case "bool":
            return (
              <S.Td key={idField} title={value?.toString()} type={type}>
                <FieldCheckboxModulo
                  key={idField}
                  id={idField}
                  propiedad={idField}
                  value={value || false}
                  handleChange={handleChangeBoolean}
                  readOnly={!editable}
                  centered={false}
                />
              </S.Td>
            );

          case "enum":
            const currentOption = {
              value: area[idField]?.Valor,
              label: area[idField]?.Etiqueta,
            };
            const options = listas[idField]?.Valores.map((option) => ({
              value: option.Valor,
              label: option.Etiqueta,
            }));
            return (
              <S.Td key={idField} title={currentOption.label} type={type}>
                <FieldList
                  id={idField}
                  type={type}
                  currentOption={currentOption}
                  options={options}
                  handleChange={handleChange}
                  readOnly={!editable || !editing}
                  required={required}
                  hiddenIndicators={true}
                />
              </S.Td>
            );

          default:
            return <S.Td key={idField}>{value}</S.Td>;
        }
      })}
      {showEditBuzon && 
      <S.Td>
        <S.Buttons>
          <button
            type="button"
            onClick={() => onEditBuzon(area.id, area['buzonCorreo'])}
          >
            <FontAwesomeIcon icon={faMailBulk} color={COLORES.BUTTON_BACKGROUND_DEFAULT} size="lg" />
          </button>
        </S.Buttons>
      </S.Td>
      }
      {type === "list" && (
        <S.Td>
          <S.Buttons>
            {editing ? (
              <>
                <button
                  key="save"
                  className="action save"
                  type="submit"
                  title="Guardar"
                >
                  ✔
                </button>
                <button
                  key="cancel"
                  className="action cancel"
                  type="button"
                  title="Cancelar"
                  onClick={() => cancelEdit()}
                >
                  ✘
                </button>
              </>
            ) : (
              <>
                <button
                  key="edit"
                  className="action edit"
                  type="button"
                  onClick={() => setEditing(!editing)}
                  disabled={!access?.Areas?.Editar || area.eliminado}
                >
                  <FontAwesomeIcon icon={faEdit} size="lg" />
                </button>
                <button
                  key="delete"
                  className="action delete"
                  type="button"
                  title="Borrar"
                  onClick={() => setDialogComentarioVisible("eliminar")}
                  disabled={!access?.Areas?.Eliminar}
                >
                  <FontAwesomeIcon icon={faTrashAlt} size="lg" />
                </button>
              </>
            )}
          </S.Buttons>
          {dialogComentarioVisible === "eliminar" && (
            <Dialog
              type="confirm"
              message="¿Quieres eliminar el área?"
              labelConfirm="Eliminar"
              confirm={eliminarArea}
              cancel={() => setDialogComentarioVisible("")}
            />
          )}
        </S.Td>
      )}
    </S.Tr>
  );
};

RegistroArea.propTypes = {
  metadataTable: PropTypes.array,
  area: PropTypes.object,
  listas: PropTypes.object,
  type: PropTypes.string,
  handleSelectionChange: PropTypes.func,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  setLoading: PropTypes.func,
  update: PropTypes.func,
  access: PropTypes.object,
  onEditBuzon: PropTypes.func,
};
